import { PsvReviewIssueStatus } from "@api/shared-runtime/enums";
import { PSV_ISSUE_DECISION_VALIDITY_MONTHS } from "@api/shared-runtime/psv-review";
import { cn } from "@/lib/utils";
import type { ReportIssue } from "../types";
import {
  changeFieldLabel,
  earlierDecisionLine,
  ISSUE_STATUS_LABELS,
  NO_VALUE,
} from "./issue-labels";

const PILL_BASE = "rounded-full px-2 py-0.5 text-xs font-medium";
const PILL_STYLES: Record<PsvReviewIssueStatus, string> = {
  [PsvReviewIssueStatus.New]:
    "bg-blue-50 text-blue-800 dark:bg-blue-950/30 dark:text-blue-200",
  [PsvReviewIssueStatus.Changed]:
    "bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200",
  [PsvReviewIssueStatus.Unchanged]: "bg-muted text-muted-foreground",
  [PsvReviewIssueStatus.Expired]:
    "bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200",
};

/** The phrase with its first letter capitalized, to start the line after the
 * status chip. */
function capitalized(phrase: string): string {
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

function IssueSentence({ issue }: { issue: ReportIssue }) {
  const { previous } = issue;
  if (issue.status === PsvReviewIssueStatus.New || !previous) {
    return null;
  }
  if (issue.status === PsvReviewIssueStatus.Expired) {
    // An expired decision may have been a Concern, so name what it was.
    return (
      <>
        {capitalized(earlierDecisionLine(previous))} — over{" "}
        {PSV_ISSUE_DECISION_VALIDITY_MONTHS} months ago, needs review
      </>
    );
  }
  if (issue.status === PsvReviewIssueStatus.Unchanged) {
    // The chip already says Unchanged.
    return <>{capitalized(earlierDecisionLine(previous))}</>;
  }
  return <>Earlier decision: {earlierDecisionLine(previous)}</>;
}

/** The label for one possible match: whether it is new, or how it compares
 * with the decision on an earlier run. */
export function CandidateIssueLabel({ issue }: { issue: ReportIssue }) {
  const note = issue.previous?.note;
  return (
    <div className="space-y-1">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={cn(PILL_BASE, PILL_STYLES[issue.status])}>
          {ISSUE_STATUS_LABELS[issue.status]}
        </span>
        <span className="text-muted-foreground">
          <IssueSentence issue={issue} />
        </span>
      </p>
      {issue.status === PsvReviewIssueStatus.Changed &&
        issue.changes.length > 0 && (
          <ul className="list-disc space-y-0.5 pl-4 text-muted-foreground">
            {issue.changes.map((change) => (
              <li key={change.field} className="break-words">
                {changeFieldLabel(change.field)}: {change.before ?? NO_VALUE} →{" "}
                {change.after ?? NO_VALUE}
              </li>
            ))}
          </ul>
        )}
      {note && (
        <p className="break-words text-muted-foreground">
          Earlier note: {note}
        </p>
      )}
    </div>
  );
}
